// R2 Option B (v1.8.49, Session 41): Kassenblatt je Floßfahrttag ohne laufende Nummer und Artikelauswahl für Barrechnungen
// (reine Funktionen; Meldungen wie Business.Kasse.Kassenblatt im Backend)

export type Belegart = 'Barrechnung' | 'Kassenblatt';

export interface KassenArtikel {
  id: number;
  name: string;
  category: string;
  grossPrice: number;
  taxRate: number;
}

export interface PositionsZeile {
  description: string;
  quantity: string;
  grossPrice: string;
  taxRate: string; // '0.19' | '0.07' | '0'
}

export const MELDUNG_KEIN_FAHRTTAG = 'Für ein Kassenblatt bitte den Floßfahrttag angeben.';
export const MELDUNG_FAHRTTAG_ZUKUNFT = 'Ein Kassenblatt kann nur für heute oder einen vergangenen Floßfahrttag erfasst werden.';
export const KASSENBLATT_HINWEIS =
  'Kassenblatt: Bareinnahmen an Bord eines Floßfahrttags (z. B. Grillgerichte, Deko, Bierkrug), ohne laufende Nummer, als PDF für die Buchhaltung.';

/** Ortsdatum als yyyy-mm-dd (für date-Eingaben). */
export const ortsdatum = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const datumDe = (iso: string): string => {
  const [j, m, t] = iso.slice(0, 10).split('-');
  return `${t}.${m}.${j}`;
};

/** Prüft Floßfahrttag (yyyy-mm-dd) gegen heute und den Termin einer zugeordneten Buchung. Meldung oder null. */
export const pruefeKassenblatt = (fahrttag: string, heute: string, buchungstermin?: string | null): string | null => {
  if (!fahrttag) return MELDUNG_KEIN_FAHRTTAG;
  if (fahrttag.slice(0, 10) > heute.slice(0, 10)) return MELDUNG_FAHRTTAG_ZUKUNFT;
  if (buchungstermin && buchungstermin.slice(0, 10) !== fahrttag.slice(0, 10)) {
    return `Der Floßfahrttag ${datumDe(fahrttag)} passt nicht zum Termin der Buchung (${datumDe(buchungstermin)}).`;
  }
  return null;
};

/** Steuersatz als Wert der Auswahl ('0.19', '0.07', '0'); unbekannte Sätze ergeben null. */
export const steuersatzWert = (satz: number): string | null => {
  const prozent = Math.round(Number(satz) * 10000) / 100;
  if (prozent === 19) return '0.19';
  if (prozent === 7) return '0.07';
  if (prozent === 0) return '0';
  return null;
};

/** Betrag für Eingabefelder mit Komma, z. B. 4,50. */
export const preisEingabe = (betrag: number): string => (Math.round(betrag * 100) / 100).toFixed(2).replace('.', ',');

/** Artikel als neue Positionszeile (Menge 1). */
export const artikelZuPosition = (a: KassenArtikel): PositionsZeile => ({
  description: a.name,
  quantity: '1',
  grossPrice: preisEingabe(a.grossPrice),
  taxRate: steuersatzWert(a.taxRate) ?? '0.19'
});

/**
 * Fügt einen Artikel hinzu: eine leere Startzeile wird ersetzt, ein bereits erfasster gleicher Artikel (Text, Preis, Satz) bekommt
 * Menge plus 1, sonst neue Zeile am Ende.
 */
export const artikelHinzufuegen = (zeilen: PositionsZeile[], a: KassenArtikel): PositionsZeile[] => {
  const neu = artikelZuPosition(a);
  const leer = (z: PositionsZeile) => !z.description.trim() && !z.grossPrice.trim();
  const index = zeilen.findIndex(
    (z) => z.description.trim() === neu.description && z.grossPrice.trim() === neu.grossPrice && z.taxRate === neu.taxRate
  );
  if (index >= 0) {
    return zeilen.map((z, i) => {
      if (i !== index) return z;
      const menge = parseInt(z.quantity, 10);
      return { ...z, quantity: String((isNaN(menge) || menge < 1 ? 0 : menge) + 1) };
    });
  }
  if (zeilen.length === 1 && leer(zeilen[0])) return [neu];
  return [...zeilen, neu];
};

/** Artikel nach Kategorie gruppieren (Reihenfolge wie geliefert). */
export const gruppiereArtikel = (artikel: KassenArtikel[]): { kategorie: string; artikel: KassenArtikel[] }[] => {
  const gruppen: { kategorie: string; artikel: KassenArtikel[] }[] = [];
  for (const a of artikel) {
    const k = a.category || 'Sonstiges';
    let g = gruppen.find((x) => x.kategorie === k);
    if (!g) {
      g = { kategorie: k, artikel: [] };
      gruppen.push(g);
    }
    g.artikel.push(a);
  }
  return gruppen;
};

/** Anzeige in der Liste: "Kassenblatt 03.10.2026" bzw. Rechnungsnummer. */
export const belegNummer = (beleg: { invoiceNumber: string; displayNumber?: string | null }): string =>
  beleg.displayNumber || beleg.invoiceNumber;

export const istKassenblatt = (beleg: { docType?: string | null }): boolean =>
  (beleg.docType || '').toLowerCase() === 'kassenblatt';

/** Dateiname des Kassenblatt-PDFs: Kassenblatt_2026-10-03.pdf, zweites Blatt des Tages Kassenblatt_2026-10-03_2.pdf. */
export const kassenblattDateiname = (tripDate: string, kennung: string): string => {
  const teile = (kennung || '').split('-');
  const nr = teile.length === 3 ? parseInt(teile[2], 10) : 1;
  return `Kassenblatt_${tripDate.slice(0, 10)}${nr > 1 ? `_${nr}` : ''}.pdf`;
};
